import * as React from "react";
import { Query } from "react-apollo";
import gql from "graphql-tag";
import { Helmet } from "react-helmet";
import { withToastManager } from "react-toast-notifications";

/** GraphQL */
import { listReports, listCategorys } from "src/graphql/queries";

import { Province, Suburb } from "../../API";

/** Presentation/UI */
import StyledButton from "../../Components/Styled/Button";
import BackendWrapper from "../../Components/Layouts/BackendWrapper";
import PageLoader from "../../Components/PageLoader";
import Link from "../../Components/Styled/Link";
import Row from "../../Components/Styled/Row";
import Card from "../../Components/Styled/Card";

/** Themes */
import { Colors } from "../../Themes";

/** Custom Types */
import { TableConsts } from "src/Utils/Consts";
import {
  CardTitle,
  CardBody,
  CardImg,
  CardText,
  Col,
  Label,
  Input
} from "reactstrap";
import Select from "react-select";
import { formatDropdown } from "../../Utils/Helpers";
import images from "src/Themes/Images";

type SelectType = {
  value: string;
  label: string;
};

type SelectProvinceType = {
  value: Province;
  label: string;
};

type SelectSuburbType = {
  value: Suburb;
  label: string;
};

type State = {
  keywordSearch: string;
  province: SelectProvinceType;
  suburb: SelectSuburbType;
  category: SelectType;
  city: string;
};

class ConsumerReports extends React.Component<State> {
  state: State = {
    keywordSearch: "",
    province: { value: Province.GAUTENG, label: "" },
    suburb: { value: Suburb.SUBURB_ONE, label: "" },
    category: { value: "", label: "" },
    city: ""
  };

  // Select Category
  selectCategory = (category: SelectType): void => {
    this.setState({ category });
  };

  // Select province
  selectProvince = (province: SelectProvinceType): void => {
    this.setState({ province });
  };

  // format constants into dropdowns for provinces
  listOfProvinces = (consts: object) => {
    return Object.keys(consts).map((item: string) => {
      return formatDropdown(item);
    });
  };

  // Select suburb
  selectSuburb = (suburb: SelectSuburbType): void => {
    this.setState({ suburb });
  };

  // format constant into dropdowns for suburbs
  listOfSuburbs = (consts: object) => {
    return Object.keys(consts).map((item: string) => {
      return formatDropdown(item);
    });
  };

  // Handle change in input fields
  handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { name, value }: { name: string; value: string } = e.currentTarget;

    switch (name) {
      case "keywordSearch":
        this.setState({ name: value });
        break;
      case "city":
        this.setState({ name: value });
        break;
      default:
        break;
    }
  };

  render() {
    const { keywordSearch, province, suburb, category, city } = this.state;
    return (
      <BackendWrapper>
        <Query query={gql(listCategorys)}>
          {({ loading, data }) => {
            if (loading) {
              return <PageLoader />;
            }
            if (!loading && data) {
              const categories = data.listCategorys.items.map((cat: any) => ({
                value: cat.id,
                label: cat.name
              }));
              return (
                <Row>
                  <Col sm={12} md={12} lg={12}>
                    <Card>
                      <Row>
                        <Col sm={12} md={12} lg={12}>
                          <p>Filters</p>
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={12} md={4} lg={4}>
                          <Label>Keyword Search</Label>
                          <Input
                            type="text"
                            name="keywordSearch"
                            value={keywordSearch}
                            id="keywordSearch"
                            placeholder="Keyword Search"
                            onChange={e =>
                              this.setState({ keywordSearch: e.target.value })
                            }
                          />
                          <br />
                        </Col>
                        <Col sm={12} md={4} lg={4}>
                          <Label>Category</Label>
                          <Select
                            options={categories}
                            onChange={this.selectCategory}
                            value={category}
                            placeholder=""
                            className="select-styling"
                          />
                        </Col>
                        <Col sm={12} md={4} lg={4}>
                          <Label>Province</Label>
                          <Select
                            options={this.listOfProvinces(Province)}
                            onChange={this.selectProvince}
                            value={province}
                            placeholder=""
                            className="select-styling"
                          />
                          <br />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={12} md={4} lg={4}>
                          <Label>City</Label>
                          <Input
                            type="text"
                            name="city"
                            value={city}
                            id="city"
                            placeholder="City Search"
                            onChange={e =>
                              this.setState({ city: e.target.value })
                            }
                          />
                          <br />
                        </Col>
                        <Col sm={12} md={4} lg={4}>
                          <Label>Suburb</Label>
                          <Select
                            options={this.listOfSuburbs(Suburb)}
                            onChange={this.selectSuburb}
                            value={suburb}
                            placeholder=""
                            className="select-styling"
                          />
                          <br />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={12} md={12} lg={12}>
                          <StyledButton
                            type="button"
                            label="Filter"
                            width="120px"
                            color={Colors.snow}
                            background={Colors.default}
                            margin="5px"
                          />
                          <StyledButton
                            type="reset"
                            label="Clear Filters"
                            width="120px"
                            color={Colors.coal}
                            background={Colors.background}
                            margin="5px"
                          />
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </Row>
              );
            } else {
              return <div>There has been an error loading filters</div>;
            }
          }}
        </Query>

        <Query
          query={gql(listReports)}
          variables={{ limit: TableConsts.limit }}
        >
          {({ loading, data }) => {
            if (loading) {
              return <PageLoader />;
            }
            if (!loading && data) {
              const reports = data.listReports.items.map((report: any) => (
                <Col
                  // TODO create Col component
                  style={{ paddingTop: "10px" }}
                  sm={12}
                  md={4}
                  lg={4}
                  key={report.id}
                >
                  <Card>
                    <CardImg variant="top" src={images.rootsLogo} />
                    <CardBody>
                      <CardText>{report.name}</CardText>
                      <CardTitle>R {report.price}</CardTitle>
                      <CardText>
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                        Nunc a facilisis enim. Duis fermentum vulputate
                        consequat. Integer eros nisi, convallis quis ex eget,
                        convallis bibendum turpis.
                      </CardText>
                    </CardBody>
                    <Row paddingBottom={"10"}>
                      <Col>
                        <Link
                          to={{
                            pathname: `/consumer/report/${report.id}`
                          }}
                          label={
                            <StyledButton
                              type="button"
                              label="View"
                              width="70px"
                              color={Colors.snow}
                              background={Colors.default}
                              margin="5px"
                            />
                          }
                        />
                        <Link
                          to={{
                            pathname: `/consumer/checkout/${report.id}`
                          }}
                          label={
                            <StyledButton
                              type="reset"
                              label="Buy"
                              width="70px"
                              color={Colors.snow}
                              background={Colors.default}
                              margin="5px"
                            />
                          }
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              ));
              return <Row paddingTop={"20"}>{reports}</Row>;
            } else {
              return <p>There has been an error loading reports</p>;
            }
          }}
        </Query>
        <Helmet title="Reports" />
      </BackendWrapper>
    );
  }
}

export default withToastManager(ConsumerReports);



// WEBPACK FOOTER //
// ./src/Containers/ConsumerReports/index.tsx